import { useState, useCallback, useRef } from "react";
import { Sparkles, Mic, Send, ArrowRight, Dumbbell, AlertTriangle, Compass } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { useSpeechInput, speak } from "@/lib/voice";
import { SpeakButton, Chips, RichText, SectionCard } from "@/components/common";

const DEPTHS = [
  { key: "quick", label: "Quick" },
  { key: "practical", label: "Practical" },
  { key: "deep", label: "Deep Dive" },
];

export default function AskPanel({ compact = false }) {
  const { mode, isSenior, isChild } = useAppMode();
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [meta, setMeta] = useState(null);
  const [depth, setDepth] = useState("practical");
  const abortRef = useRef(null);

  const onVoice = useCallback((t) => setQ(t), []);
  const { listening, supported, start } = useSpeechInput(onVoice);

  const streamAnswer = async (question, d) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setText("");
    setStreaming(true);
    let acc = "";
    try {
      const res = await fetch(`${API}/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("lifeos_token")}` },
        body: JSON.stringify({ question, mode, depth: d }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setText(acc);
      }
      if ((isSenior || isChild) && acc) speak(acc);
    } catch (e) {
      if (e.name !== "AbortError") { setText("Sorry, I couldn't answer just now. Please try again."); toast.error("Connection issue."); }
    } finally {
      setStreaming(false);
    }
  };

  const submit = async (question) => {
    const t = (question ?? q).trim();
    if (!t) return;
    setQ(t);
    setSubmitted(t);
    setMeta(null);
    setDepth("practical");
    streamAnswer(t, "practical");
    try {
      const { data } = await api.post("/ask/meta", { question: t, mode });
      if (!data.error) setMeta(data);
    } catch (_) {}
  };

  const changeDepth = (d) => { setDepth(d); streamAnswer(submitted, d); };

  const big = isSenior;

  return (
    <div className="space-y-6">
      <div className={`relative rounded-lg border-2 border-primary/40 bg-card p-2 sm:p-3 shadow-lg shadow-primary/5 ${big ? "p-4" : ""}`}>
        <div className="flex items-start gap-2">
          <Sparkles className={`text-primary shrink-0 mt-3 ml-1 ${big ? "w-7 h-7" : "w-5 h-5"}`} />
          <textarea
            data-testid="ask-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            rows={big ? 3 : 2}
            placeholder={isChild ? "Ask me anything! Like: How do I make friends?" : isSenior ? "Type your question, or tap the microphone to speak." : "What do you need help with?"}
            className={`flex-1 resize-none bg-transparent outline-none placeholder:text-muted-foreground py-2.5 ${big ? "text-xl" : "text-base"}`}
          />
        </div>
        <div className="flex items-center justify-between gap-2 mt-1 px-1">
          {supported ? (
            <button onClick={start} data-testid="ask-mic" aria-label="Speak your question"
              className={`inline-flex items-center gap-2 rounded-full px-3 ${big ? "py-3 text-base" : "py-2 text-sm"} font-semibold border transition-colors ${listening ? "bg-destructive text-destructive-foreground border-destructive animate-pulse" : "border-border hover:border-primary"}`}>
              <Mic className={big ? "w-5 h-5" : "w-4 h-4"} /> {listening ? "Listening…" : "Speak"}
            </button>
          ) : <span />}
          <button onClick={() => submit()} disabled={streaming || !q.trim()} data-testid="ask-submit"
            className={`inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 ${big ? "py-3.5 text-lg" : "py-2.5"} disabled:opacity-40 hover:brightness-110 active:scale-95 transition`}>
            {isChild ? "Go!" : "Ask inizio"} <Send className={big ? "w-5 h-5" : "w-4 h-4"} />
          </button>
        </div>
      </div>

      {meta?.safety_note && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4" data-testid="safety-note">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm">{meta.safety_note}</p>
        </div>
      )}

      {(submitted || streaming) && (
        <div className="space-y-5 animate-rise">
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex border-b border-border">
              {DEPTHS.map((l) => (
                <button key={l.key} onClick={() => changeDepth(l.key)} disabled={streaming} data-testid={`level-${l.key === "deep" ? "detailed" : l.key}`}
                  className={`px-4 sm:px-5 py-3 text-sm font-head font-bold transition-colors disabled:opacity-50 ${depth === l.key ? "text-primary border-b-2 border-primary -mb-px" : "text-muted-foreground hover:text-foreground"}`}>
                  {l.label}
                </button>
              ))}
              <div className="ml-auto self-center pr-3"><SpeakButton text={text} /></div>
            </div>
            <div className="p-5 sm:p-6" data-testid="ask-answer">
              <div className={big ? "text-xl leading-relaxed" : ""}>
                <RichText text={text} />
                {streaming && <span className="inline-block w-2 h-5 align-middle bg-primary ml-0.5 animate-pulse" />}
              </div>
            </div>
          </div>

          {meta?.next_step && (
            <SectionCard title="Your next step" icon={ArrowRight} accent testid="next-step">
              <p className={big ? "text-xl" : "text-base"}>{meta.next_step}</p>
            </SectionCard>
          )}
          {meta?.practice && (
            <SectionCard title="Practice this" icon={Dumbbell} testid="practice">
              <p className="leading-relaxed">{meta.practice}</p>
            </SectionCard>
          )}
          {meta?.related_skills?.length > 0 && (
            <SectionCard title="Explore connected skills" icon={Compass} testid="related-skills">
              <Chips items={meta.related_skills} onClick={(s) => submit(`Teach me about ${s}`)} />
            </SectionCard>
          )}
        </div>
      )}

      {!submitted && !streaming && !compact && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground font-head">Try one of these:</p>
          <Chips
            items={isChild
              ? ["How do I make friends?", "What should I do when I feel angry?", "Teach me about saving money"]
              : isSenior
              ? ["Is this message a scam?", "How do I make a video call?", "How do I stay safe online?"]
              : ["How do I start budgeting?", "How do I prepare for an interview?", "Why do I procrastinate?", "Give me a startup idea"]}
            onClick={(s) => submit(s)}
          />
        </div>
      )}
    </div>
  );
}
